"use client"

import { useState, useEffect } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface BannerImage {
  desktop: string
  mobile: string
  alt: string
  link?: string
}

interface BannerSliderProps {
  images: BannerImage[]
  autoplaySpeed?: number
}

export default function BannerSlider({ images, autoplaySpeed = 5000 }: BannerSliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [validImages, setValidImages] = useState<BannerImage[]>(images)
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set())
  
  // Filter out images that fail to load
  useEffect(() => {
    if (imageErrors.size > 0) {
      const filtered = images.filter((_, index) => !imageErrors.has(index))
      setValidImages(filtered)
      // Reset current index if it's out of bounds
      if (currentIndex >= filtered.length && filtered.length > 0) {
        setCurrentIndex(0)
      }
    } else {
      setValidImages(images)
    }
  }, [imageErrors, images, currentIndex])
  
  // Handle automatic slideshow
  useEffect(() => {
    if (validImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % validImages.length)
    }, autoplaySpeed)
    
    return () => clearInterval(interval)
  }, [autoplaySpeed, validImages.length])
  
  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }
  
  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? validImages.length - 1 : currentIndex - 1
    setCurrentIndex(newIndex)
  }
  
  const goToNext = () => {
    const newIndex = (currentIndex + 1) % validImages.length
    setCurrentIndex(newIndex)
  }
  
  const handleImageError = (index: number) => {
    console.warn(`Banner image ${index} failed to load, hiding it from slider`)
    setImageErrors(prev => new Set(prev).add(index))
  }

  const handleBannerClick = (link?: string) => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  }
  
  if (validImages.length === 0) return null;
  
  return (
    <div className="group relative w-full overflow-hidden rounded-lg container mx-auto px-4">
      {/* Desktop Images */}
      <div className="relative w-full hidden md:block rounded-lg overflow-hidden">
        {validImages.map((image, index) => (
          <div 
            key={`desktop-${index}`}
            className={`transition-opacity duration-1000 ${index === currentIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
            style={index === currentIndex ? {} : { pointerEvents: 'none' }}
          >
            <img
              src={image.desktop}
              alt={image.alt}
              className={`w-full h-auto rounded-lg ${image.link ? 'cursor-pointer' : ''}`}
              onClick={() => handleBannerClick(image.link)}
              onError={() => handleImageError(index)}
            />
          </div>
        ))}
      </div>
      
      {/* Mobile Images */}
      <div className="relative w-full md:hidden rounded-lg overflow-hidden">
        {validImages.map((image, index) => (
          <div 
            key={`mobile-${index}`}
            className={`transition-opacity duration-1000 ${index === currentIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
            style={index === currentIndex ? {} : { pointerEvents: 'none' }}
          >
            <img
              src={image.mobile || image.desktop}
              alt={image.alt}
              className={`w-full h-auto rounded-lg ${image.link ? 'cursor-pointer' : ''}`}
              onClick={() => handleBannerClick(image.link)}
              onError={() => handleImageError(index)}
            />
          </div>
        ))}
      </div>
      
      {/* Navigation Arrows - Only show if multiple images */}
      {validImages.length > 1 && (
        <>
          <button 
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 focus:outline-none transition-all opacity-0 group-hover:opacity-100"
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>
          
          <button 
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 focus:outline-none transition-all opacity-0 group-hover:opacity-100"
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>
          
          {/* Dots/Bullets */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
            {validImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full focus:outline-none transition-colors ${index === currentIndex ? 'bg-white' : 'bg-white/50'}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
